// Browser-side client for Pro's session-token broker endpoint (`/session/token`).
//
// Pro is the single source of session truth. This client fetches a fresh access
// token from the broker — which refreshes Pro's OIDC identity server-side — caches
// it, and refetches shortly before it expires. The cache means the broker is only
// hit around expiry, not on every API call.
//
// Resilience contract: a flaky network must never log an editor out and lose
// unsaved work. Transient broker failures (network errors, timeouts, 5xx) are
// retried with backoff while the last valid cached token keeps being used. ONLY an
// explicit 401 from the broker means the Pro session is genuinely gone — that, and
// only that, triggers `onSessionInvalid`.

import {debugEvent} from "./CommonComponents";

const DEFAULT_BROKER_URL = "/session/token.json";
export const DEFAULT_REFRESH_SKEW_MS = 60_000; // refetch this long before expiry

// Randomised extra lead time, re-drawn per refresh, so that several surfaces sharing
// one Pro session (typically a few editor tabs) do not converge on the same refresh
// instant and present the same refresh token at the same moment. Pro rotates refresh
// tokens and treats reuse as a compromise signal, revoking the WHOLE token family —
// so simultaneous refreshes do not merely waste a call, they end the session.
//
// COUPLING, do not break: Pro's own refresh margin must stay strictly greater than
// `DEFAULT_REFRESH_SKEW_MS + MAX_REFRESH_JITTER_MS`. If it is not, the broker hands
// back a token this client immediately judges stale and every API call becomes a
// round trip until Pro's threshold trips. Pro's side of this is
// `OIDC_REFRESH_MARGIN` in backend/pro (see the harden-pro-session-refresh change, D1).
export const MAX_REFRESH_JITTER_MS = 30_000;
const DEFAULT_MAX_RETRIES = 5; // transient-failure retries before giving up
const DEFAULT_BACKOFF_BASE_MS = 500;
const DEFAULT_BACKOFF_MAX_MS = 5_000;
// Used only if the broker omits expires_at AND the token has no exp. Must stay
// comfortably above DEFAULT_REFRESH_SKEW_MS + MAX_REFRESH_JITTER_MS: at 60_000 it exactly
// equalled the skew, so such a token was judged stale the instant it arrived and every
// API call refetched. Unreachable in practice today, but it was a latent trap.
const FALLBACK_TTL_MS = 180_000;

export interface SessionTokenBrokerOptions {
  /** Called only on an explicit broker 401 — the Pro session is gone. Redirect to login here. */
  onSessionInvalid: () => void;
  /** Defaults to the same-origin "/session/token.json". */
  brokerUrl?: string;
  /** Injectable for tests; defaults to the global fetch with credentials included. */
  fetchFn?: typeof fetch;
  refreshSkewMs?: number;
  /**
   * Upper bound on the per-refresh randomised lead time. Set to 0 to disable jitter.
   * Keep `refreshSkewMs + maxJitterMs` strictly below Pro's own refresh margin.
   */
  maxJitterMs?: number;
  maxRetries?: number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  /** Injectable for tests. */
  sleepFn?: (ms: number) => Promise<void>;
  /** Injectable for tests; defaults to Math.random. Must return [0, 1). */
  randomFn?: () => number;
}

/**
 * Latched session state. `"invalid"` once an authoritative broker 401 has been
 * observed; cleared back to `"active"` by the next successful refresh. While
 * `"invalid"`, automatic refresh is suspended so a dead session doesn't drive a
 * request-per-second from the editor's 1s sync loop.
 */
export type SessionState = "active" | "invalid";

export interface SessionTokenBroker {
  /**
   * Resolves a usable access token, refreshing via the broker when needed.
   * While the session is latched `"invalid"` this rejects immediately with
   * `SessionInvalidError` WITHOUT a network call — use `retry()` to force a real
   * refresh attempt (e.g. after reauth completes or the tab becomes active).
   */
  getAccessToken: () => Promise<string | undefined>;
  /** Drop the cached token so the next getAccessToken refetches (e.g. after a backend 401). */
  invalidate: () => void;
  /** Current latched session state. */
  getSessionState: () => SessionState;
  /**
   * Subscribe to session-state transitions (`"active"` <-> `"invalid"`). Fires
   * on change only, with the new state. Returns an unsubscribe function.
   */
  onSessionStateChange: (listener: (state: SessionState) => void) => () => void;
  /**
   * Force one real refresh attempt regardless of the latch. On success caches
   * the fresh token and clears the latch back to `"active"`; on another 401
   * stays `"invalid"`. Concurrent callers share the single in-flight attempt.
   */
  retry: () => Promise<string | undefined>;
  /**
   * The login-session (refresh-token) expiry from the most recent broker response,
   * as epoch milliseconds, or undefined when the broker didn't provide one
   * (legacy/guest, non-decodable refresh token, or not yet fetched).
   */
  getSessionExpiresAt: () => number | undefined;
  /**
   * "Now" in epoch milliseconds, corrected for a wrongly-set local clock using the
   * server's `server_now` from the most recent response. Falls back to the raw
   * local clock when no `server_now` has been seen.
   */
  serverNow: () => number;
}

interface CachedToken {
  token: string;
  expiresAtMs: number;
  /**
   * Extra lead time for THIS cached token, drawn in `[0, maxJitterMs]` when it was
   * fetched. Added to `refreshSkewMs` when judging freshness so concurrent surfaces
   * on one session refresh at different moments. Always non-negative, so it can only
   * make the client refresh *earlier* than `expiresAtMs - refreshSkewMs`, never later
   * — a token is therefore never served past its expiry because of jitter.
   */
  jitterMs: number;
}

interface BrokerResponse {
  access_token?: string;
  expires_at?: number; // epoch seconds (short-lived access token)
  session_expires_at?: number; // epoch seconds (login session / refresh token)
  server_now?: number; // epoch seconds (server clock when the response was produced)
}

/** Thrown when the broker explicitly reports the Pro session is invalid (HTTP 401). */
export class SessionInvalidError extends Error {
  constructor() {
    super("Pro session is no longer valid");
    this.name = "SessionInvalidError";
  }
}

function decodeBase64(b64: string): string {
  if (typeof atob === "function") {
    return atob(b64);
  }
  // Node / non-browser fallback (typed loosely to avoid a hard @types/node dep)
  const nodeBuffer = (globalThis as {Buffer?: {from(s: string, enc: string): {toString(enc: string): string}}}).Buffer;
  if (nodeBuffer) {
    return nodeBuffer.from(b64, "base64").toString("binary");
  }
  throw new Error("no base64 decoder available");
}

/** Decodes a JWT's `exp` claim (epoch seconds). Returns undefined for opaque/malformed tokens. */
export function jwtExpSeconds(token: string | undefined): number | undefined {
  if (!token) {
    return undefined;
  }
  const parts = token.split(".");
  if (parts.length < 2) {
    return undefined;
  }
  try {
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // JWT segments are base64url with '=' padding stripped — restore it so even a
    // strict base64 decoder accepts the payload.
    const remainder = payload.length % 4;
    if (remainder === 2) {
      payload += "==";
    } else if (remainder === 3) {
      payload += "=";
    }
    const json = JSON.parse(decodeBase64(payload));
    return typeof json.exp === "number" ? json.exp : undefined;
  } catch {
    return undefined;
  }
}

/** True when a JWT is within `skewSeconds` of expiry. Opaque tokens (no exp) are treated as valid. */
export function isJwtExpired(token: string | undefined, skewSeconds = 30): boolean {
  const exp = jwtExpSeconds(token);
  if (exp === undefined) {
    return false;
  }
  return Date.now() >= exp * 1000 - skewSeconds * 1000;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Pulls the HTTP status off a broker error (attached by `fetchOnce`); undefined for network errors. */
function httpStatusOf(err: unknown): number | undefined {
  const status = (err as {httpStatus?: unknown} | null | undefined)?.httpStatus;
  return typeof status === "number" ? status : undefined;
}

export function createSessionTokenBroker(opts: SessionTokenBrokerOptions): SessionTokenBroker {
  const brokerUrl = opts.brokerUrl ?? DEFAULT_BROKER_URL;
  const doFetch: typeof fetch = opts.fetchFn ?? ((input, init) => fetch(input, init));
  const refreshSkewMs = opts.refreshSkewMs ?? DEFAULT_REFRESH_SKEW_MS;
  const maxJitterMs = opts.maxJitterMs ?? MAX_REFRESH_JITTER_MS;
  const randomFn = opts.randomFn ?? Math.random;
  const maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
  const backoffBaseMs = opts.backoffBaseMs ?? DEFAULT_BACKOFF_BASE_MS;
  const backoffMaxMs = opts.backoffMaxMs ?? DEFAULT_BACKOFF_MAX_MS;
  const sleep = opts.sleepFn ?? defaultSleep;

  let cached: CachedToken | undefined;
  let inflight: Promise<string | undefined> | undefined;
  let sessionState: SessionState = "active";
  const stateListeners = new Set<(state: SessionState) => void>();
  // Clock-skew correction: offset (ms) between the server's `server_now` and the
  // local clock, recomputed on each successful fetch. 0 until a response with
  // `server_now` is seen (fallback = raw local clock, i.e. no regression).
  let offsetMs = 0;
  // Login-session (refresh-token) expiry in epoch ms from the last response.
  let sessionExpiresAtMs: number | undefined;

  function serverNow(): number {
    return Date.now() + offsetMs;
  }

  function setSessionState(next: SessionState) {
    if (sessionState === next) {
      return;
    }
    sessionState = next;
    stateListeners.forEach((listener) => listener(next));
  }

  // Freshness is judged against the skew-corrected server time, so a wrongly-set
  // local clock doesn't refetch prematurely or serve an already-expired token.
  //
  // The effective lead time is `refreshSkewMs + jitter`. The jitter is drawn per
  // cached token (see fetchOnce) rather than once per page load, so two tabs that
  // happen to start together still drift apart on their next refresh instead of
  // staying locked in step.
  function isFresh(c: CachedToken | undefined): c is CachedToken {
    return !!c && serverNow() < c.expiresAtMs - refreshSkewMs - c.jitterMs;
  }

  async function fetchOnce(): Promise<CachedToken> {
    const resp = await doFetch(brokerUrl, {
      method: "GET",
      credentials: "include",
      headers: {Accept: "application/json"},
    });
    if (resp.status === 401) {
      throw new SessionInvalidError();
    }
    if (!resp.ok) {
      const err = new Error(`session token broker returned ${resp.status}`);
      (err as {httpStatus?: number}).httpStatus = resp.status;
      throw err;
    }
    const body: BrokerResponse = await resp.json();
    if (!body.access_token) {
      throw new Error("session token broker response missing access_token");
    }
    // Clock-skew offset from the server's `server_now` (recomputed each fetch);
    // and the login-session expiry, both from this fresh response.
    if (typeof body.server_now === "number") {
      offsetMs = body.server_now * 1000 - Date.now();
    } else {
      offsetMs = 0; // no server reference in this response → fall back to the raw local clock (don't keep a stale offset)
    }
    sessionExpiresAtMs = typeof body.session_expires_at === "number" ? body.session_expires_at * 1000 : undefined;
    const expSeconds = body.expires_at ?? jwtExpSeconds(body.access_token);
    const expiresAtMs = expSeconds ? expSeconds * 1000 : serverNow() + FALLBACK_TTL_MS;
    return {token: body.access_token, expiresAtMs, jitterMs: drawJitterMs(expiresAtMs)};
  }

  /**
   * Draws this token's extra lead time. Clamped so the jitter can never consume more
   * of the token's life than it has: for a token whose remaining life is already at or
   * below the skew, jitter is 0 rather than pushing the refresh point further into the
   * past (which would make no difference, but the clamp keeps the arithmetic honest and
   * keeps the FALLBACK_TTL_MS case sane).
   */
  function drawJitterMs(expiresAtMs: number): number {
    if (maxJitterMs <= 0) {
      return 0;
    }
    const remaining = expiresAtMs - serverNow() - refreshSkewMs;
    if (remaining <= 0) {
      return 0;
    }
    return Math.floor(randomFn() * Math.min(maxJitterMs, remaining));
  }

  async function refresh(): Promise<string | undefined> {
    // Telemetry: only actual refresh fetches are instrumented here — cache hits in
    // getAccessToken() fire on every API call and would drown the signal.
    const startedAt = Date.now();
    let lastError: unknown;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        cached = await fetchOnce();
        // A successful refresh clears any latched session-invalid state.
        setSessionState("active");
        debugEvent("sessionTokenRefreshSuccess", {attempt, durationMs: Date.now() - startedAt});
        return cached.token;
      } catch (err) {
        if (err instanceof SessionInvalidError) {
          // Explicit, authoritative "logged out": clear cache, latch invalid so
          // subsequent getAccessToken calls fail fast without a network storm,
          // notify state listeners + the (legacy) onSessionInvalid callback.
          // Never retried here.
          cached = undefined;
          setSessionState("invalid");
          debugEvent("sessionTokenRefreshFailure", {outcome: "sessionInvalid", httpStatus: 401, attempt});
          opts.onSessionInvalid();
          throw err;
        }
        // Transient failure: prefer keeping the user working over failing — but
        // only serve the stale token while it is still valid in SERVER time, so a
        // behind-the-server local clock can't serve an already-expired token
        // (consistent with isFresh()).
        lastError = err;
        if (cached && serverNow() < cached.expiresAtMs) {
          debugEvent("sessionTokenRefreshFailure", {outcome: "transientServedStale", httpStatus: httpStatusOf(err), attempt});
          return cached.token;
        }
        if (attempt < maxRetries) {
          await sleep(Math.min(backoffMaxMs, backoffBaseMs * 2 ** attempt));
        }
      }
    }
    // Transient outage with no usable cached token: surface a (retryable) error,
    // NOT a logout — callers/react-query retry and recover when the broker returns.
    debugEvent("sessionTokenRefreshFailure", {outcome: "transientExhausted", httpStatus: httpStatusOf(lastError), attempt: maxRetries});
    throw lastError ?? new Error("session token broker unavailable");
  }

  // Dedupe concurrent refreshes so a burst of API calls triggers one fetch.
  function refreshDeduped(): Promise<string | undefined> {
    if (!inflight) {
      inflight = refresh().finally(() => {
        inflight = undefined;
      });
    }
    return inflight;
  }

  return {
    getAccessToken() {
      if (isFresh(cached)) {
        return Promise.resolve(cached.token);
      }
      if (sessionState === "invalid") {
        // Latched invalid: fail fast, no network call (avoids a request storm
        // from the 1s sync loop). Recovery is driven explicitly via retry().
        return Promise.reject(new SessionInvalidError());
      }
      return refreshDeduped();
    },
    invalidate() {
      cached = undefined;
    },
    getSessionState() {
      return sessionState;
    },
    onSessionStateChange(listener) {
      stateListeners.add(listener);
      return () => {
        stateListeners.delete(listener);
      };
    },
    retry() {
      // Force a real refresh regardless of the latch (post-reauth / tab-active).
      return refreshDeduped();
    },
    getSessionExpiresAt() {
      return sessionExpiresAtMs;
    },
    serverNow,
  };
}
